import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("sir_knight_session");
  if (!id) {
    id = `sk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("sir_knight_session", id);
  }
  return id;
};

const quickReplies = [
  "How long does credit repair take?",
  "What are your prices?",
  "Is credit repair legal?",
  "Schedule a consultation",
];

const SirKnightChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Greetings! I'm Sir Knight, your AI credit-repair guide. How may I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const userText = (text ?? input).trim();
    if (!userText || loading) return;
    setMessages(m => [...m, { role: "user", content: userText }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId.current, message: userText });
      setMessages(m => [...m, { role: "assistant", content: res.data.reply || "Hmm, my apologies — try again?" }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Apologies, I'm having trouble reaching the realm. Please call 1 (888) 96-KNIGHT for immediate help." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Open chat"
        className={`fixed left-5 bottom-5 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all ${open ? "bg-knight-navy text-white" : "bg-knight-gold text-knight-navy"}`}
        style={{ boxShadow: "0 12px 32px -4px rgba(11, 28, 92, 0.5)" }}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white animate-pulse" />}
      </button>

      {/* Chat window */}
      {open && (
        <div className="fixed left-5 bottom-24 z-40 w-[360px] max-w-[calc(100vw-40px)] h-[540px] max-h-[calc(100vh-120px)] bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="knight-gradient text-white p-4 flex items-center gap-3 relative overflow-hidden">
            <div className="absolute inset-0 grain-overlay opacity-30" />
            <div className="relative w-11 h-11 rounded-full bg-knight-gold flex items-center justify-center text-knight-navy">
              <Sparkles size={20} />
            </div>
            <div className="relative">
              <p className="font-display font-bold text-lg leading-tight">Sir Knight</p>
              <p className="text-xs text-white/70 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online · AI Credit Guide
              </p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-knight-navy text-white rounded-br-sm" : "bg-white border border-slate-200 text-knight-navy rounded-bl-sm shadow-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-knight-navy/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-knight-navy/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 rounded-full bg-knight-navy/50 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Quick replies */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {quickReplies.map((q, i) => (
                <button key={i} onClick={() => send(q)} className="text-[11px] px-2.5 py-1.5 rounded-full bg-knight-navy/5 hover:bg-knight-navy hover:text-white text-knight-navy/80 font-medium transition-colors">
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 bg-white flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask Sir Knight anything..."
              className="flex-1 text-sm px-4 py-2.5 rounded-full border border-slate-200 focus:border-knight-navy focus:outline-none"
            />
            <button type="submit" disabled={loading || !input.trim()} className="w-10 h-10 rounded-full bg-knight-gold hover:bg-knight-gold/90 text-knight-navy disabled:opacity-50 flex items-center justify-center flex-shrink-0">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default SirKnightChat;
